import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import ArrowsVertical from '@/assets/icons/arrows_vertical_line.svg?component';
import Building from '@/assets/icons/building_line.svg?component';
import CornerDownLeft from '@/assets/icons/corner_down_left_line.svg?component';
import Document from '@/assets/icons/document_line.svg?component';
import LocationFill from '@/assets/icons/location_fill.svg?component';
import Round from '@/assets/icons/round_line.svg?component';
import VideoCamera from '@/assets/icons/video_camera_line.svg?component';
import { SvgIcon, type SvgIconProps } from '@/components/atoms/SvgIcon/SvgIcon';

import type { QuickSearchItem, QuickSearchItemKind } from './types';

import styles from './QuickSearch.module.scss';

export interface QuickSearchRowProps {
    item: QuickSearchItem;
    isActive: boolean;
    onSelect: (item: QuickSearchItem) => void;
    onHover: () => void;
}

const KIND_ICONS: Record<QuickSearchItemKind, SvgIconProps['icon']> = {
    client: Building,
    location: LocationFill,
    cage: Round,
    bioscope: VideoCamera,
    winch: ArrowsVertical,
    page: Document,
};

function renderItemIcon(item: QuickSearchItem) {
    if (item.icon) {
        return (
            <span className={styles.itemIcon}>
                <SvgIcon icon={item.icon} size="custom" customSize={18} color="currentColor" />
            </span>
        );
    }

    return (
        <SvgIcon
            icon={KIND_ICONS[item.kind]}
            size="inherit"
            className={styles.itemIcon}
            svgProps={{ 'aria-hidden': true }}
        />
    );
}

export default function QuickSearchRow(props: QuickSearchRowProps) {
    const { item, isActive, onSelect, onHover } = props;
    const { t } = useTranslation();

    return (
        <div
            className={cn(styles.item, { [styles.active]: isActive })}
            data-active={isActive}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(item)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(item);
                }
            }}
            onMouseEnter={onHover}>
            <div className={styles.itemContent}>
                {renderItemIcon(item)}
                <span className={styles.itemLabel}>
                    {item.parentLabel ? (
                        <>
                            <span className={styles.itemParent}>{item.parentLabel}</span>
                            <span className={styles.itemSeparator} aria-hidden="true">
                                {t('quickSearch.parentSeparator', '›')}
                            </span>
                        </>
                    ) : null}
                    <span>{item.label}</span>
                </span>
                {item.status && (
                    <span
                        className={cn(styles.statusPill, {
                            [styles.statusActive]: item.status === 'active',
                            // An empty pen reads like an inactive one, as in the overview table.
                            [styles.statusInactive]:
                                item.status === 'inactive' || item.status === 'empty',
                            [styles.statusHistorical]: item.status === 'historical',
                        })}>
                        {t(`quickSearch.status.${item.status}`, item.status)}
                    </span>
                )}
            </div>
            {isActive && (
                <span className={styles.enterHint}>
                    {t('quickSearch.enterHint', 'Enter')}{' '}
                    <SvgIcon
                        icon={CornerDownLeft}
                        size="inherit"
                        svgProps={{ 'aria-hidden': true }}
                    />
                </span>
            )}
        </div>
    );
}
